<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import IndividualLayout from '@/modules/investing/components/individuals/individual-layout.vue'
import VSelectInvestor from '@/modules/investing/components/VSelectInvestor.vue'
import DealCard from '@/modules/dashboard/components/deal-card.vue'
import { useIndividualStore } from '@/modules/investing/stores/better-individual-store'
import { useDealStore } from '@/modules/dashboard/stores/deal-store'
import { VSection, VStats, VStatsHero, VTabListSimple } from '@/modules/shared/components'
import { initialMoney } from '@/modules/shared/utils/money'
import { useHiddenMetrics } from '@/modules/workspace/composables/use-hidden-metrics'
import AllocationChart from '@/modules/shared/components/allocation-chart.vue'
import PerformerChart from '@/modules/shared/components/performer-chart.vue'
import { rails_url } from '@/modules/shared/utils/rails'

///////////////////////////////////////////////////////////////////////////////
// Calls
///////////////////////////////////////////////////////////////////////////////

const { hidden } = useHiddenMetrics()
const authStore = useAuthStore()
const individualStore = useIndividualStore()
const dealStore = useDealStore()
const individual = computed(() => individualStore.individual)

const selectedCids = computed(() =>
  individualStore.selectedShareholderKeys.map((keys) => JSON.parse(keys).reverse().join(':')),
)

///////////////////////////////////////////////////////////////////////////////
// Insights
///////////////////////////////////////////////////////////////////////////////

// collect and transform into camelCase
const individualInsights = computed(() => {
  const insights = individual.value?.insights

  if (!insights) {
    return {
      initialValue: initialMoney,
      currentValue: initialMoney,
      cashPosition: initialMoney,
      distributedNet: initialMoney,
      moic: 0,
      roi: 0,
      companies: [],
      industries: [],
    }
  }

  return {
    initialValue: insights.initial_value,
    currentValue: insights.current_value,

    cashPosition: insights.cash_position,
    distributedNet: insights.distributed_net,

    moic: insights.moic,
    roi: insights.roi,

    companies: insights.companies,
    industries: insights.industries,
  }
})

///////////////////////////////////////////////////////////////////////////////
// Actions
///////////////////////////////////////////////////////////////////////////////

const { individual_id, slug } = useRoute().params as { individual_id: string; slug: string }

const fetch = async (shareholder_cids = null) => {
  showSkeleton()
  await individualStore.fetchIndividual(individual_id, { slug, shareholder_cids })

  if (shareholder_cids === null)
    shareholder_cids = individualStore.listShareholders.map((shareholder) => `${shareholder.type}:${shareholder.id}`)
  ;(await dealStore.fetchDeals({ is_featured: true, limit: 3, order: 'asc' }), hideSkeleton())
}

const onFilter = async () => {
  await fetch(selectedCids.value)
}

const deals = computed(() => (skeleton.value ? [{}, {}, {}, {}, {}] : dealStore.deals))

///////////////////////////////////////////////////////////////////////////////
// Portfolio Charts
///////////////////////////////////////////////////////////////////////////////

const portfolioChartTabIndex = ref(0)

const chartTabs = computed(() => {
  const tabs = ['Portfolio Allocation', 'Industry Allocation']
  if (!hidden.value.roi) tabs.push('Top Performers')
  return tabs
})

///////////////////////////////////////////////////////////////////////////////
// Authorization
///////////////////////////////////////////////////////////////////////////////

const isAdmin = computed(() => true)

///////////////////////////////////////////////////////////////////////////////
// Main
///////////////////////////////////////////////////////////////////////////////

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

///////////////////////////////////////////////////////////////////////////////
// Deals
///////////////////////////////////////////////////////////////////////////////

const can_view_featured_deals = computed(() => is_current_investor.value)

const has_featured_deals = computed(() => deals.value.length > 0)

const is_current_investor = computed(() => ['active', 'past-due'].includes(authStore.current_user.status))

onMounted(async () => {
  await fetch()
})
</script>

<template>
  <IndividualLayout selectedTab="overview">
    <VSection class="rounded border-l-4 border-[#85B5C9] bg-[#ABD0DF] px-2 py-3 sm:px-4">
      <div class="flex flex-col-reverse justify-start gap-2 sm:flex-row sm:items-center">
        <VSelectInvestor
          v-model="individualStore.selectedShareholderKeys"
          :investors="individualStore.listShareholders"
          :onClose="onFilter"
        />

        <p class="text-sm font-medium text-gray-700">
          Use this filter to view your portfolio as a specific investor or vehicle
        </p>
      </div>
    </VSection>

    <VSection label="Featured Deals" v-if="has_featured_deals && can_view_featured_deals">
      <template #actions>
        <a :href="`${rails_url()}/list/featured`" class="text-sm text-[#3D8A9D] underline">View All</a>
      </template>
      <div class="flex max-w-full gap-3 overflow-x-auto">
        <DealCard v-for="deal in deals" :deal="deal" :skeleton="skeleton" />
      </div>
    </VSection>

    <div class="gap-10 sm:flex">
      <VSection label="360° Portfolio overview" class="sm:w-1/2">
        <VStatsHero
          class="mb-6"
          :skeleton="skeleton"
          :currentValue="individualInsights.currentValue"
          :initialValue="individualInsights.initialValue"
          :hiddenFields="[hidden.initialValue ? 'initial' : null]"
        />
        <VStats
          :skeleton="skeleton"
          :stats="[
            {
              colspan: 2,
              label: 'ROI',
              type: 'percent',
              value: individualInsights.roi,
              visible: !hidden.roi,
            },
            {
              colspan: 2,
              label: 'MOIC',
              type: 'multiple',
              value: individualInsights.moic,
              visible: !hidden.moic,
            },
            {
              colspan: 2,
              label: 'Cash position',
              type: 'currency',
              value: individualInsights.cashPosition,
              visible: !hidden.cashPosition,
            },
            {
              colspan: 2,
              label: 'Distributed',
              type: 'currency',
              value: individualInsights.distributedNet,
              visible: !hidden.distributions,
            },
          ]"
        />
      </VSection>

      <VSection class="sm:w-1/2">
        <VTabListSimple
          @update:current="(index: number) => (portfolioChartTabIndex = index)"
          :current="portfolioChartTabIndex"
          :tabs="chartTabs"
          class="-mt-2"
        />
        <div class="mt-6 rounded-lg border border-gray-200 bg-white p-2 sm:p-6">
          <AllocationChart
            v-if="portfolioChartTabIndex === 0"
            :items="individualInsights.companies"
            :skeleton="skeleton"
            :isAdmin="isAdmin"
          />
          <AllocationChart
            v-if="portfolioChartTabIndex === 1"
            :items="individualInsights.industries"
            :skeleton="skeleton"
            :isAdmin="isAdmin"
          />
          <PerformerChart
            v-if="portfolioChartTabIndex === 2 && !hidden.roi"
            :items="individualInsights.companies"
            :skeleton="skeleton"
            :total_roi="individualInsights.roi"
          />
        </div>
      </VSection>
    </div>
  </IndividualLayout>
</template>
